import { observeIntersection } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useLiveData } from '@toeverything/infra';
import {
  type CSSProperties,
  forwardRef,
  memo,
  type ReactNode,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import type { PDF } from '../entities/pdf';
import type { PDFPage } from '../entities/pdf-page';
import type { PageSize, PDFPageTextContent } from '../renderer/types';
import { PdfPageBitmapSurface, PDFPageCanvas } from './components';
import {
  readPdfPageBitmapCache,
  writePdfPageBitmapCache,
} from './page-bitmap-cache';
import {
  type PDFPageHighlightRect,
  toHighlightRectStyle,
} from './pdf-highlight-overlay';
import * as styles from './styles.css';

let pdfPageRendererInstanceSeq = 0;

interface PDFPageProps {
  pdf: PDF;
  pageNum: number;
  actualSize: PageSize;
  maxSize: PageSize;
  viewportInfo: PageSize;
  resize: (
    viewportInfo: PageSize,
    actualSize: PageSize,
    maxSize: PageSize,
    isThumbnail?: boolean
  ) => { aspectRatio: number } & PageSize;
  scale?: number;
  className?: string;
  onSelect?: (pageNum: number) => void;
  onHighlightClick?: (
    rect: PDFPageHighlightRect,
    position: { left: number; top: number }
  ) => void;
  resolveHighlightRects?: (
    rects: PDFPageHighlightRect[],
    text: PDFPageTextContent | null,
    pageNum: number
  ) => PDFPageHighlightRect[];
  preload?: boolean;
  isThumbnail?: boolean;
  highlightRects?: PDFPageHighlightRect[];
  overlay?: ReactNode;
}

export const PDFPageRenderer = memo(function PDFPageRenderer({
  pdf,
  pageNum,
  className,
  actualSize,
  maxSize,
  viewportInfo,
  onSelect,
  onHighlightClick,
  resolveHighlightRects,
  resize,
  preload,
  isThumbnail,
  highlightRects,
  overlay,
  scale = window.devicePixelRatio,
}: PDFPageProps) {
  const t = useI18n();
  const rendererInstanceIdRef = useRef<string | null>(null);
  rendererInstanceIdRef.current ??= `pdf-page-renderer-${++pdfPageRendererInstanceSeq}`;
  const pageViewRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [page, setPage] = useState<PDFPage | null>(null);
  const img = useLiveData(useMemo(() => (page ? page.bitmap$ : null), [page]));
  const text = useLiveData(
    useMemo(
      () => (page && !isThumbnail ? page.text$ : null),
      [isThumbnail, page]
    )
  );
  const error = useLiveData(page?.error$ ?? null);
  const size = useMemo(
    () => resize(viewportInfo, actualSize, maxSize, isThumbnail),
    [resize, viewportInfo, actualSize, maxSize, isThumbnail]
  );
  const [visibility, setVisibility] = useState(false);
  const hideVisibilityTimerRef = useRef<number | null>(null);
  const shouldRender = !isThumbnail || visibility || preload;
  const shouldLoadText = !isThumbnail && shouldRender;
  const pagePoolKey = `${rendererInstanceIdRef.current}:${pageNum}`;
  const visibleText = shouldLoadText ? (text ?? null) : null;
  const resolvedHighlightRects = useMemo(
    () =>
      resolveHighlightRects
        ? resolveHighlightRects(highlightRects ?? [], visibleText, pageNum)
        : (highlightRects ?? []),
    [highlightRects, pageNum, resolveHighlightRects, visibleText]
  );

  const bitmapCacheRole = isThumbnail ? 'thumbnail' : 'page';
  // Fresh render wins; otherwise show the best cached variant for this page
  // (CSS-scaled to the current layout) instead of the loading skeleton.
  const displayImg =
    img ??
    readPdfPageBitmapCache(
      pdf.id,
      pageNum,
      bitmapCacheRole,
      Math.ceil(size.width * scale)
    );

  useEffect(() => {
    if (!img) return;
    writePdfPageBitmapCache(pdf.id, pageNum, bitmapCacheRole, img);
  }, [bitmapCacheRole, img, pageNum, pdf.id]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!displayImg) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = displayImg.width;
    canvas.height = displayImg.height;
    ctx.drawImage(displayImg, 0, 0);
  }, [displayImg]);

  useEffect(() => {
    if (!shouldRender) return;
    if (!page) return;

    const width = size.width;
    const height = size.height;
    if (width * height === 0) return;

    page.render({
      width,
      height,
      scale,
    });

    return () => {
      // reset, not unsubscribe: unsubscribe kills the effect pipeline for good,
      // so the next run of this hook (zoom commit, sidebar resize) would emit
      // into a dead pipeline and the page would keep its stale low-res bitmap.
      page.render.reset();
    };
  }, [page, scale, shouldRender, size.height, size.width]);

  useEffect(() => {
    if (!shouldLoadText) return;
    if (!page) return;

    page.loadText();

    return () => {
      page.loadText.reset();
    };
  }, [page, shouldLoadText]);

  useEffect(() => {
    if (!shouldRender) return;
    if (!pdf) return;

    const { page, release } = pdf.page(pageNum, pagePoolKey);

    setPage(page);

    return () => {
      release();
      setPage(null);
    };
  }, [pagePoolKey, pageNum, pdf, shouldRender]);

  useEffect(() => {
    const pageView = pageViewRef.current;
    if (!pageView) return;

    if (isElementInsideViewport(pageView)) {
      setVisibility(true);
    }

    return observeIntersection(pageView, entry => {
      if (entry.isIntersecting) {
        if (hideVisibilityTimerRef.current !== null) {
          window.clearTimeout(hideVisibilityTimerRef.current);
          hideVisibilityTimerRef.current = null;
        }
        setVisibility(true);
        return;
      }

      if (hideVisibilityTimerRef.current !== null) {
        window.clearTimeout(hideVisibilityTimerRef.current);
      }
      hideVisibilityTimerRef.current = window.setTimeout(() => {
        hideVisibilityTimerRef.current = null;
        setVisibility(false);
      }, 377);
    });
  }, []);

  useEffect(() => {
    return () => {
      if (hideVisibilityTimerRef.current !== null) {
        window.clearTimeout(hideVisibilityTimerRef.current);
      }
    };
  }, []);

  return (
    <div
      ref={pageViewRef}
      className={className}
      data-pdf-page-frame-index={pageNum}
      style={size}
      onClick={() => onSelect?.(pageNum)}
    >
      <PageRendererInner
        img={displayImg}
        ref={canvasRef}
        err={error ? t['com.streamlain.pdf.page.render.error']() : null}
        displaySize={size}
        pageNum={pageNum}
        text={visibleText}
        highlightRects={isThumbnail ? [] : resolvedHighlightRects}
        overlay={overlay}
        onHighlightClick={onHighlightClick}
      />
    </div>
  );
});

function isElementInsideViewport(element: HTMLElement) {
  const bounds = element.getBoundingClientRect();
  return (
    bounds.bottom >= 0 &&
    bounds.right >= 0 &&
    bounds.top <= window.innerHeight &&
    bounds.left <= window.innerWidth
  );
}

interface PageRendererInnerProps {
  img: ImageBitmap | null;
  err: string | null;
  displaySize: PageSize;
  pageNum: number;
  text?: PDFPageTextContent | null;
  highlightRects?: PDFPageHighlightRect[];
  overlay?: ReactNode;
  onHighlightClick?: (
    rect: PDFPageHighlightRect,
    position: { left: number; top: number }
  ) => void;
}

const PageRendererInner = forwardRef<HTMLCanvasElement, PageRendererInnerProps>(
  (
    {
      img,
      err,
      displaySize,
      pageNum,
      text,
      highlightRects,
      overlay,
      onHighlightClick,
    },
    ref
  ) => {
    if (img) {
      return (
        <div
          className={styles.pdfPageCanvasLayer}
          data-pdf-page-index={pageNum}
          style={
            {
              width: displaySize.width,
              height: displaySize.height,
              aspectRatio: `${displaySize.width} / ${displaySize.height}`,
              // Glyph font sizes derive from this var so a zoom commit only
              // changes one custom property instead of reconciling thousands
              // of glyph spans with new inline styles.
              '--pdf-page-height': `${displaySize.height}px`,
            } as CSSProperties
          }
        >
          <PDFPageCanvas
            ref={ref}
            style={{
              width: '100%',
              height: '100%',
            }}
          />
          <PDFHighlightOverlay
            rects={highlightRects ?? []}
            onHighlightClick={onHighlightClick}
          />
          <PDFTextLayer text={text ?? null} />
          {overlay}
        </div>
      );
    }

    if (err) {
      return <p className={styles.pdfPageError}>{err}</p>;
    }

    return (
      <PdfPageBitmapSurface
        bitmap={null}
        displayWidth={displaySize.width}
        displayHeight={displaySize.height}
        data-pdf-page-cache="false"
      />
    );
  }
);

PageRendererInner.displayName = 'pdf-page-renderer-inner';

// Memoized on the text content only: page resizes (zoom) must not reconcile
// the glyph spans — their geometry is fully relative (% + --pdf-page-height).
const PDFTextLayer = memo(function PDFTextLayer({
  text,
}: {
  text: PDFPageTextContent | null;
}) {
  if (!text?.glyphs.length) return null;

  return (
    <div className={styles.pdfPageTextLayer} data-pdf-text-layer="true">
      {text.glyphs.map((glyph, index) => (
        <span
          key={`${index}:${glyph.text}:${glyph.left}:${glyph.top}`}
          className={styles.pdfPageTextGlyph}
          data-pdf-text-glyph="true"
          data-pdf-glyph-index={index}
          style={{
            left: `${glyph.left * 100}%`,
            top: `${glyph.top * 100}%`,
            width: `${glyph.width * 100}%`,
            height: `${glyph.height * 100}%`,
            fontSize: `max(1px, calc(var(--pdf-page-height) * ${glyph.height}))`,
          }}
        >
          {glyph.text}
        </span>
      ))}
    </div>
  );
});

export function PDFHighlightOverlay({
  rects,
  onHighlightClick,
}: {
  rects: PDFPageHighlightRect[];
  onHighlightClick?: (
    rect: PDFPageHighlightRect,
    position: { left: number; top: number }
  ) => void;
}) {
  if (rects.length === 0) return null;

  return (
    <div
      className={styles.pdfPageHighlightOverlay}
      aria-hidden={onHighlightClick ? undefined : 'true'}
    >
      {rects.map((rect, index) => (
        <div
          key={`${rect.x}:${rect.y}:${rect.width}:${rect.height}:${index}`}
          className={styles.pdfPageHighlightRect}
          data-active={rect.active ? 'true' : undefined}
          data-comment={rect.commentId ? 'true' : undefined}
          data-highlight={rect.highlightId ? 'true' : undefined}
          data-kind={rect.kind}
          style={toHighlightRectStyle(rect)}
          onPointerDown={event => {
            if (!rect.commentId && !rect.highlightId) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onClick={event => {
            if (!rect.commentId && !rect.highlightId) return;
            event.stopPropagation();
            onHighlightClick?.(rect, {
              left: event.clientX,
              top: event.clientY,
            });
          }}
        />
      ))}
    </div>
  );
}
